import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Heart, Share2, Home } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAudioPlayback } from '@/hooks/useAudioPlayback';
import { ExpertPanel } from '@/components/player/ExpertPanel';
import { PlaybackControls } from '@/components/player/PlaybackControls';
import { QuotesList } from '@/components/player/QuotesList';

const AudioPlayer = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { podcastId } = useParams();

  const {
    podcast, loading, isPlaying, isFavorite, audioUrl,
    activeSpeaker, revealed, quotes, audioRef, progress,
    togglePlay, handleTimeUpdate, handleLoadedMetadata, handleSeek, handleEnded,
    toggleFavorite, handleShare
  } = useAudioPlayback(podcastId, token);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0A0A0B' }}>
        <div className="w-12 h-12 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!podcast) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4" style={{ backgroundColor: '#0A0A0B' }}>
        <p className="text-gray-400">Podcast not found</p>
        <Button onClick={() => navigate('/')} variant="ghost" className="text-purple-400">Go Home</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-8" style={{ backgroundColor: '#0A0A0B' }}>
      {/* Grid BG */}
      <div className="fixed inset-0 opacity-[0.03] pointer-events-none" style={{
        backgroundImage: `linear-gradient(rgba(109,40,217,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(109,40,217,0.3) 1px, transparent 1px)`,
        backgroundSize: '50px 50px'
      }} />

      {/* Header */}
      <header className="sticky top-0 z-20 border-b px-4 sm:px-6 py-3" style={{ borderColor: 'rgba(255,255,255,0.06)', backgroundColor: 'rgba(10,10,11,0.9)', backdropFilter: 'blur(20px)' }}>
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-gray-400 hover:text-white hover:bg-white/5" data-testid="back-home-btn">
            <Home className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Home</span>
          </Button>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 px-3 py-1 rounded-full" style={{ backgroundColor: isPlaying ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.05)' }}>
              <div className={`w-2 h-2 rounded-full ${isPlaying ? 'bg-red-500 animate-pulse' : 'bg-gray-600'}`} />
              <span className="text-[10px] font-bold tracking-wider" style={{ color: isPlaying ? '#EF4444' : '#64748B' }}>
                {isPlaying ? 'ON AIR' : 'PAUSED'}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleFavorite} className={`${isFavorite ? 'text-red-400' : 'text-gray-400'} hover:bg-white/5`} data-testid="favorite-btn">
              <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
            </Button>
            <Button variant="ghost" size="icon" onClick={handleShare} className="text-gray-400 hover:bg-white/5" data-testid="share-btn">
              <Share2 className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 relative z-10">
        {/* Topic */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
          <p className="text-xs tracking-[0.2em] uppercase mb-2" style={{ color: '#6D28D9' }}>Opposite Direction Episode</p>
          <h1 className="text-2xl sm:text-4xl font-light tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#F8FAFC' }} data-testid="podcast-topic">
            {podcast.topic}
          </h1>
        </motion.div>

        <ExpertPanel podcast={podcast} activeSpeaker={activeSpeaker} revealed={revealed} />

        <PlaybackControls
          isPlaying={isPlaying}
          audioUrl={audioUrl}
          progress={progress}
          togglePlay={togglePlay}
          handleSeek={handleSeek}
        />

        {/* Hidden audio element */}
        <audio
          ref={audioRef}
          src={audioUrl}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={handleEnded}
        />

        <QuotesList quotes={quotes} podcastTopic={podcast.topic} />
      </div>
    </div>
  );
};

export default AudioPlayer;
